// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
global.__base = __dirname + "/";

import "@fontsource/work-sans";

import(/* webpackPreload: true */ "@/assets/scss/style.scss");
import(/* webpackPreload: true */ "bootstrap-sass");

import axios from "axios";

import Vue from "vue";

import * as Sentry from "@sentry/vue";
import Vuex from "vuex";
import vuexI18n from "vuex-i18n";

import VCalendar from "v-calendar";
import VueScrollTo from "vue-scrollto";
import VueFullscreen from "vue-fullscreen";
import VueDOMPurifyHTML from "vue-dompurify-html";
import vOutsideEvents from "vue-outside-events";

import VueMq from "vue-mq";
import Snotify from "vue-snotify";
import { ClientTable, ServerTable } from "vue-tables-2";

import router from "@/router";
import store from "@/stores/store";
import { getLabelResponse } from "@/stores/helpers/labelsHelper";
import VueTour from "vue-tour";

import App from "./App";
import MobileDetect from "./views/MobileDetect";

import { StreamCore } from "@/plugins/streamCore";
import { loadDefaultLanguage, DEFAULT_LANG } from "@/utility/LocalizationService";

import { getEnv, version, enableSentry, getUrl } from "./common/config";

Vue.config.productionTip = false;

loadDefaultLanguage();

Vue.use(Vuex);
Vue.use(vOutsideEvents);
Vue.use(VueMq, {
  breakpoints: {
    // default breakpoints
    sm: 450,
    md: 1250,
    lg: Infinity
  }
});
Vue.use(Snotify);
Vue.use(ClientTable);
Vue.use(ServerTable);
Vue.use(VueTour);
Vue.use(VueDOMPurifyHTML);

Vue.use(VCalendar);
Vue.use(VueScrollTo);
Vue.use(VueFullscreen);

Vue.use(vuexI18n.plugin, store, { warnings: false });

Vue.use(
  new StreamCore({
    debug: false,
    vuex: {
      store,
      actionPrefix: "STREAM_",
      mutationPrefix: "STREAM_"
    }
  })
);

const sentryOptions = {
  Vue,
  dsn: "https://3f794ecd36404489b8012e3d69a3e4ea@sentry.scmgroup.com/3",
  environment: getEnv()
};

// when VUE_APP_VERSION env is defined send it to sentry
// should be defined only on live environment
if (version) {
  sentryOptions.release = version;
}

if (enableSentry) {
  //init sentry
  Sentry.init(sentryOptions);
}

/*
 * Axios general interceptor
 */
const token = localStorage.getItem("token");
const language = localStorage.getItem("language");
if (token) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
}

axios.defaults.headers.common["Accept-Language"] = language ? language : DEFAULT_LANG;

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    const originalRequest = error.config;
    const orgId = localStorage.organizationCode;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url.indexOf("external-auth/refresh") === -1 &&
      originalRequest.url.indexOf("auth/refresh") === -1 &&
      originalRequest.url.indexOf("login") === -1
    ) {
      originalRequest._retry = true;

      const externalRefreshToken = window.localStorage.getItem("externalRefreshToken");
      const refreshToken = window.localStorage.getItem("refreshToken");

      if (!refreshToken && !externalRefreshToken) {
        return Promise.reject(error);
      }

      const path = externalRefreshToken
        ? `${getUrl()}/api/external-auth/refresh`
        : `${getUrl()}/api/${orgId}/auth/refresh`;
      const options = externalRefreshToken
        ? { refreshToken: externalRefreshToken }
        : { token: refreshToken };

      return axios
        .post(path, options)
        .then(({ data }) => {
          const data_token = externalRefreshToken ? data.result.accessToken : data.result.token;
          const data_refreshToken = data.result.refreshToken;
          localStorage.setItem("token", data_token);
          localStorage.setItem("refreshToken", data_refreshToken);
          if (externalRefreshToken) {
            localStorage.setItem("externalAccessToken", data.result.externalAccessToken);
            localStorage.setItem("externalRefreshToken", data.result.externalRefreshToken);
          }
          axios.defaults.headers.common["Authorization"] = `Bearer ${data_token}`;
          originalRequest.headers["Authorization"] = `Bearer ${data_token}`;
          return axios(originalRequest);
        })
        .catch(() => {
          router.push({ name: "login" }).catch(() => {});
          return Promise.reject(error);
        });
    }

    errorResponseHandler(error);

    return Promise.reject(error);
  }
);

/* eslint-disable no-new */
const vueApp = new Vue({
  el: "#app",
  router,
  store,
  components: {
    App,
    MobileDetect
  },
  created() {
    const getHTMLTag = document.documentElement;
    getHTMLTag.setAttribute("lang", navigator.language.split("-")[0]);
  },
  template: "<MobileDetect><App /></MobileDetect>"
});

// Evals errors axios
function errorResponseHandler(error) {
  if (error.response && ["put", "post", "delete"].indexOf(error.config.method) > -1) {
    const e = { message: "Error" };
    const lbl = getLabelResponse(error);

    if (localStorage.getItem("gerErrorResultMessage")) {
      e.message = localStorage.getItem("gerErrorResultMessage");
    } else if (error.response.status === 502 || error.response.status === 503) {
      e.message = vueApp.$t(vueApp.$t("errors.service_not_available"));
      e.type = "service_not_available";
    } else if (lbl) {
      e.message = vueApp.$t("errors." + lbl.toLowerCase());
    } else if (!!error.response.data.result && error.response.data.result.error) {
      e.message = error.response.data.result.error;
    } else if (error.response.data.message) {
      // Es. err 403 Forbidden
      e.message = vueApp.$t(error.response.data.message);
    } else if (error.response.data.error) {
      // Es. oauth activation
      e.message = error.response.data.error;
    }

    // -- VueApp here is defined!! --
    // 12/9/2019 HACK Hide error from googleapis
    // 23/10/2019 Hide error 401 (Not authorized) errors
    if (error.config.url.indexOf("iid.googleapis.com") === -1 && error.response.status !== 401) {
      vueApp.$store.dispatch("Alert/Errors/setVal", e, { root: true });
    }
  }
}
